import React from 'react'
import { cn } from '@/shared/lib/utils'
import { Character } from '@storyverse/shared'
import { ChevronRight, Users } from 'lucide-react'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'

import boyImg from '@/assets/images/characters/boy.png'
import girlImg from '@/assets/images/characters/girl.png'

interface CharacterCardProps {
    character: Character
    onClick?: () => void
}

import { PremiumCard } from '@/shared/components/ui/premium-card'

export const CharacterCard = ({ character, onClick }: CharacterCardProps) => {
    const navigate = useNavigate()
    const { project_id } = useParams()

    const handleNavigate = () => {
        if (onClick) {
            onClick()
        } else {
            navigate(`/workspace/${project_id}/characters/${character.id}`)
        }
    }

    const isFemale = character.gender?.toLowerCase().includes('girl') ||
        character.gender?.toLowerCase().includes('woman') ||
        character.gender?.toLowerCase().includes('female');

    const fallbackImage = isFemale ? girlImg : boyImg

    // Get role and image from default variant
    const defaultVariant = character.variants?.find(v => v.isDefault) || character.variants?.[0]
    const role = defaultVariant?.role

    const [imageUrl, setImageUrl] = React.useState<string>(fallbackImage)

    React.useEffect(() => {
        let objectUrl: string | null = null;
        let activeImage: any = null;

        if (defaultVariant) {
            if (defaultVariant.active_image_type === 'ai') {
                activeImage = defaultVariant.look_test_image;
            } else if (defaultVariant.active_image_type === 'user') {
                activeImage = defaultVariant.user_image;
            } else {
                // Fallback to whatever is available
                activeImage = defaultVariant.user_image || defaultVariant.look_test_image;
            }
        }

        if (!activeImage) {
            setImageUrl(fallbackImage);
        } else if (typeof activeImage === 'string') {
            setImageUrl(activeImage);
        } else if (activeImage instanceof Blob || activeImage instanceof File) {
            objectUrl = URL.createObjectURL(activeImage);
            setImageUrl(objectUrl);
        } else {
            setImageUrl(fallbackImage);
        }

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [defaultVariant, fallbackImage]);

    return (
        <PremiumCard
            title={character.fullname}
            image={imageUrl}
            onClick={handleNavigate}
            badge={role}
        />
    )
}
